import { useState } from 'react'
import { Descriptions, Drawer, Select, Table, App } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { payslipsApi, payrollPeriodsApi } from '@/features/hr/api'
import type { PaySlip } from '@/shared/types/hr'
import PageHeader from '@/shared/components/PageHeader'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default function PayslipPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const periodId = searchParams.get('period_id') ?? ''
  const [selectedSlip, setSelectedSlip] = useState<PaySlip | null>(null)

  const { data: periods } = useQuery({
    queryKey: ['payroll-periods-all'],
    queryFn: () => payrollPeriodsApi.list({}),
  })

  const { data: payslips, isLoading } = useQuery({
    queryKey: ['payslips', periodId],
    queryFn: () => payslipsApi.list(periodId),
    enabled: !!periodId,
  })

  const periodOptions = periods?.map((p) => ({
    label: `${p.plant_name} — ${MONTHS[p.month - 1]} ${p.year} (${p.status})`,
    value: p.id,
  })) ?? []

  const columns = [
    { title: 'Code', dataIndex: 'emp_code', key: 'emp_code', width: 100 },
    { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
    { title: 'Present', dataIndex: 'days_present', key: 'days_present', width: 75 },
    { title: 'Absent', dataIndex: 'days_absent', key: 'days_absent', width: 75 },
    { title: 'OT Hrs', dataIndex: 'overtime_hours', key: 'overtime_hours', width: 75 },
    { title: 'Basic', dataIndex: 'basic_earned', key: 'basic_earned', width: 100 },
    { title: 'Gross', dataIndex: 'gross', key: 'gross', width: 100 },
    { title: 'PF', dataIndex: 'pf_deduction', key: 'pf_deduction', width: 80 },
    { title: 'ESI', dataIndex: 'esi_deduction', key: 'esi_deduction', width: 80 },
    {
      title: 'Net Pay', dataIndex: 'net_pay', key: 'net_pay', width: 110,
      render: (v: string) => <strong>{v}</strong>,
    },
    {
      title: '', key: 'view', width: 60,
      render: (_: unknown, record: PaySlip) => (
        <a onClick={() => setSelectedSlip(record)}>View</a>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title="Pay Slips" subtitle="View computed pay slips for a payroll period" />

      <div style={{ marginBottom: 16 }}>
        <Select
          showSearch optionFilterProp="label"
          placeholder="Select payroll period"
          options={periodOptions}
          value={periodId || undefined}
          onChange={(v) => setSearchParams({ period_id: v })}
          style={{ width: 400 }}
        />
      </div>

      {periodId ? (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={payslips}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 1000 }}
          summary={(data) => {
            if (!data.length) return null
            const totals = data.reduce(
              (acc, s) => ({
                gross: acc.gross + Number(s.gross),
                pf: acc.pf + Number(s.pf_deduction),
                esi: acc.esi + Number(s.esi_deduction),
                net: acc.net + Number(s.net_pay),
              }),
              { gross: 0, pf: 0, esi: 0, net: 0 },
            )
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}><strong>Totals</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={6}><strong>{totals.gross.toFixed(2)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={7}><strong>{totals.pf.toFixed(2)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={8}><strong>{totals.esi.toFixed(2)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={9}><strong>{totals.net.toFixed(2)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={10} />
              </Table.Summary.Row>
            )
          }}
        />
      ) : (
        <p style={{ color: '#999' }}>Select a payroll period to view pay slips.</p>
      )}

      <Drawer title="Pay Slip Detail" open={!!selectedSlip} onClose={() => setSelectedSlip(null)} width={480}>
        {selectedSlip && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Employee">{selectedSlip.emp_code} — {selectedSlip.employee_name}</Descriptions.Item>
            <Descriptions.Item label="Days Present">{selectedSlip.days_present}</Descriptions.Item>
            <Descriptions.Item label="Days Absent">{selectedSlip.days_absent}</Descriptions.Item>
            <Descriptions.Item label="OT Hours">{selectedSlip.overtime_hours}</Descriptions.Item>
            <Descriptions.Item label="Basic Earned">{selectedSlip.basic_earned}</Descriptions.Item>
            <Descriptions.Item label="HRA">{selectedSlip.hra}</Descriptions.Item>
            <Descriptions.Item label="DA">{selectedSlip.da}</Descriptions.Item>
            <Descriptions.Item label="OT Amount">{selectedSlip.overtime_amount}</Descriptions.Item>
            <Descriptions.Item label="Gross">{selectedSlip.gross}</Descriptions.Item>
            <Descriptions.Item label="PF Deduction">{selectedSlip.pf_deduction}</Descriptions.Item>
            <Descriptions.Item label="ESI Deduction">{selectedSlip.esi_deduction}</Descriptions.Item>
            <Descriptions.Item label="TDS">{selectedSlip.tds}</Descriptions.Item>
            <Descriptions.Item label="Other Deductions">{selectedSlip.other_deductions}</Descriptions.Item>
            <Descriptions.Item label="Net Pay"><strong style={{ fontSize: 16 }}>{selectedSlip.net_pay}</strong></Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  )
}
